<style lang="scss" scoped>
@import '$vars';

.medical-history-section {
  @apply flex flex-col items-center content-start justify-start;
  @apply w-full;
  margin-bottom: 2px;

  &__header {
    @apply flex flex-row items-center justify-between;
    @apply w-full p-2;
    @apply cursor-pointer;
    background: rgb(236, 240, 241);
  }

  &__title {
    @apply m-0 p-0;
    @apply text-grayDark;
    font-family: $font1;
    font-size: 0.8rem;
    font-weight: 600;
  }

  &__content {
    @apply w-full p-2;
  }

  &__icon {
    transition: all 0.3s ease;

    &--up {
      transform: rotate(180deg);
      transition: all 0.3s ease;
    }
  }
}

.medical-records {
  @apply mt-2;

  &__title {
    @apply text-base text-grayDark;
    font-family: $font1;
    font-weight: 600;
  }

  &__not-found {
    @apply text-base text-grayDark text-left pt-4;
    font-weight: 500;
  }

  &__list {
    @apply flex flex-row flex-wrap items-center justify-start;
  }

  &__file {
    @apply p-2 mr-4 mb-4;
    @apply self-stretch flex flex-row items-center justify-start relative;
    background: rgb(244, 244, 244);
    width: 30%;

    @screen sm {
      @apply w-full;
    }
  }

  &__icon {
    @apply cursor-pointer;
    width: 40px;

    img {
      height: 100%;
      width: auto;
    }
  }

  &__description {
    @apply cursor-pointer;
  }

  &__type {
    @apply text-grayDark mb-0;
    font-family: $font1;
    font-size: 0.8rem;
    font-weight: 600;
    text-transform: capitalize;
  }

  &__date {
    @apply text-grayDark;
    font-size: 0.65rem;
  }

  &__signed {
    @apply absolute cursor-pointer;
    right: -2%;
    top: -10%;

    &-tooltip {
      @apply absolute p-1;
      background: rgb(84, 221, 132);
      bottom: calc(100% + 10px);
      color: #ffffff;
      left: -32px;
      text-align: center;
      font-size: 0.65rem;
      text-transform: uppercase;
      font-weight: 600;
      width: 100px;

      &:after {
        content: '';
        border: 5px solid transparent;
        border-top-color: rgb(84, 221, 132);
        display: block;
        position: absolute;
        top: 100%;
        right: calc(45px);
      }
    }
  }
}
</style>

<template>
  <section
    v-if="medicalRecords.length > 0"
    class="medical-history-section"
  >
    <header
      class="medical-history-section__header"
      @click="ui.isOpen = !ui.isOpen"
    >
      <h2 class="medical-history-section__title">
        Prontuários disponíveis para download
      </h2>

      <i
        :class="{ 'medical-history-section__icon--up': ui.isOpen }"
        class="icon icon-arrow-down medical-history-section__icon"
      />
    </header>

    <div
      v-if="ui.isOpen"
      ref="history-section-notes"
      class="medical-history-section__content"
    >
      <section class="medical-records">
        <dr-loader
          v-if="ui.isLoading"
          size="lg"
          class="mt-2"
        />

        <template v-if="!ui.isLoading && medicalRecords.length === 0">
          <p class="medical-records__not-found">
            Nenhum prontuário encontrado.
          </p>
        </template>

        <template v-if="!ui.isLoading && medicalRecords.length > 0">
          <div class="medical-records__list">
            <div
              v-for="file in medicalRecords"
              :key="`medical-record-file-${file.id}`"
              :class="{ 'pointer-events-none': ui.isLoadingFile }"
              class="medical-records__file"
            >
              <div
                class="medical-records__icon"
                @click="downloadFile(file)"
              >
                <img src="@/assets/img/pdf.png" />
              </div>

              <div
                class="medical-records__description"
                @click="downloadFile(file)"
              >
                <h5 class="medical-records__type">
                  Prontuário Médico
                </h5>

                <div class="medical-records__date">
                  Emitido em {{ $moment(file['created_at']).format('DD/MM/YYYY [às] HH:MM') }}
                </div>
              </div>
            </div>
          </div>
        </template>
      </section>
    </div>
  </section>
</template>

<script>
import Axios from 'axios'
export default {
  name: 'MedicalHistoryDownloadRecord',

  props: {
    appointmentId: {
      type: Number,
      required: true
    },

    patient: {
      type: Object,
      required: false,
      default: () => ({ name: 'Paciente' })
    }
  },

  data () {
    return {
      ui: {
        isLoading: false,
        isLoadingFile: false,
        isOpen: false
      },
      medicalRecords: []
    }
  },

  watch: {
    'ui.isOpen': {
      deep: false,
      immediate: false,
      handler (next) {
        if (next) {
          this.fetch()
        }
      }
    }
  },

  created () {
    this.fetch()
  },

  methods: {
    fetch () {
      const endpoint = `/doctor/medical_record_download?filterAnd[mrd.agenda]=${
        this.appointmentId
      }&filterAnd[mrd.status]=SUCCESS`

      this.fn_SHOW_PROGRESS()
      this.ui.isLoading = true

      this.$http
        .get(endpoint, this.$store.getters.getAuthHeader)
        .then((res) => {
          this.medicalRecords = res.data.data
        })
        .finally(() => {
          this.ui.isLoading = false
          this.fn_HIDE_PROGRESS()
        })
    },

    downloadFile (file) {
      if (!file) {
        return null
      }

      this.fn_SHOW_PROGRESS()
      this.ui.isLoadingFile = true

      const url = `${this.API_URL}/s3/file/medical_record_download/${file.uuid}/uuid`

      Axios.get(url, {
        headers: this.$store.getters.getAuthHeader.headers,
        responseType: 'blob'
      }).then(res => {
        const blob = new Blob([res.data], { type: 'application/pdf' })
        const href = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = href
        link.download = `prontuario_${this.patient.name.replace(' ', '-')}_${this.$moment(file['created_at']).format('DD-MM-YYYY')}.pdf`
        link.target = '_blank'
        link.click()
      })
        .catch((err) => this.fn_HANDLE_ERROR(err))
        .finally(() => {
          this.fn_HIDE_PROGRESS()
          this.ui.isLoadingFile = false
        })
    }
  }
}
</script>
